import { useEffect, useState } from "react";
import { Pencil, Trash2 } from "lucide-react";
import {
  fetchSubjects,
  fetchTopics,
  fetchSubtopics,
  createTopic,
  createSubtopic,
  updateTopic,
  deleteTopic,
  updateSubtopic,
  deleteSubtopic,
  type Topic,
  type SubTopic,
} from "../api/worker";
import { useModal } from "../components/ModalProvider";

export function TopicsScreen() {
  const { showConfirm, showSuccess, showError } = useModal();
  const [subjects, setSubjects] = useState<{ id: string; name: string }[]>([]);
  const [selectedSubject, setSelectedSubject] = useState("");
  const [topics, setTopics] = useState<Topic[]>([]);
  const [selectedTopic, setSelectedTopic] = useState("");
  const [subtopics, setSubtopics] = useState<SubTopic[]>([]);

  const [newTopic, setNewTopic] = useState("");
  const [newSubtopic, setNewSubtopic] = useState("");
  const [saving, setSaving] = useState(false);

  // Inline edit state
  const [editingTopicId, setEditingTopicId] = useState<string | null>(null);
  const [editingTopicName, setEditingTopicName] = useState("");
  const [editingSubtopicId, setEditingSubtopicId] = useState<string | null>(null);
  const [editingSubtopicName, setEditingSubtopicName] = useState("");

  useEffect(() => {
    fetchSubjects()
      .then((r) => setSubjects(r.subjects))
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!selectedSubject) {
      setTopics([]);
      return;
    }
    fetchTopics(selectedSubject)
      .then((r) => setTopics(r.topics))
      .catch(() => {});
  }, [selectedSubject]);

  useEffect(() => {
    if (!selectedTopic) {
      setSubtopics([]);
      return;
    }
    fetchSubtopics(selectedTopic)
      .then((r) => setSubtopics(r.subtopics))
      .catch(() => {});
  }, [selectedTopic]);

  // ── Topic CRUD ─────────────────────────────────────────────────────

  const addTopic = async () => {
    if (!newTopic.trim() || !selectedSubject || saving) return;
    setSaving(true);
    try {
      const topic = await createTopic(selectedSubject, newTopic.trim());
      setTopics((prev) => [...prev, topic]);
      setNewTopic("");
    } catch {
      showError("Error", "Failed to create topic.");
    } finally {
      setSaving(false);
    }
  };

  const startEditTopic = (t: Topic) => {
    setEditingTopicId(t.id);
    setEditingTopicName(t.name);
  };

  const saveEditTopic = async () => {
    if (!editingTopicId || !editingTopicName.trim() || saving) return;
    setSaving(true);
    try {
      const updated = await updateTopic(editingTopicId, editingTopicName.trim());
      setTopics((prev) => prev.map((t) => (t.id === editingTopicId ? updated : t)));
      setEditingTopicId(null);
      showSuccess("Updated", "Topic renamed successfully.");
    } catch {
      showError("Error", "Failed to update topic.");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteTopic = async (t: Topic) => {
    const ok = await showConfirm(
      "Delete Topic",
      `Delete "${t.name}"? This will also remove all its subtopics. Questions under this topic will lose their topic assignment.`,
    );
    if (!ok) return;
    try {
      await deleteTopic(t.id);
      setTopics((prev) => prev.filter((x) => x.id !== t.id));
      if (selectedTopic === t.id) {
        setSelectedTopic("");
        setSubtopics([]);
      }
      showSuccess("Deleted", `Topic "${t.name}" deleted.`);
    } catch {
      showError("Error", "Failed to delete topic.");
    }
  };

  // ── Subtopic CRUD ──────────────────────────────────────────────────

  const addSubtopic = async () => {
    if (!newSubtopic.trim() || !selectedTopic || saving) return;
    setSaving(true);
    try {
      const st = await createSubtopic(selectedTopic, newSubtopic.trim());
      setSubtopics((prev) => [...prev, st]);
      setNewSubtopic("");
    } catch {
      showError("Error", "Failed to create subtopic.");
    } finally {
      setSaving(false);
    }
  };

  const startEditSubtopic = (st: SubTopic) => {
    setEditingSubtopicId(st.id);
    setEditingSubtopicName(st.name);
  };

  const saveEditSubtopic = async () => {
    if (!editingSubtopicId || !editingSubtopicName.trim() || saving) return;
    setSaving(true);
    try {
      const updated = await updateSubtopic(editingSubtopicId, editingSubtopicName.trim());
      setSubtopics((prev) => prev.map((st) => (st.id === editingSubtopicId ? updated : st)));
      setEditingSubtopicId(null);
      showSuccess("Updated", "Subtopic renamed successfully.");
    } catch {
      showError("Error", "Failed to update subtopic.");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteSubtopic = async (st: SubTopic) => {
    const ok = await showConfirm(
      "Delete Subtopic",
      `Delete "${st.name}"? Questions under this subtopic will lose their subtopic assignment.`,
    );
    if (!ok) return;
    try {
      await deleteSubtopic(st.id);
      setSubtopics((prev) => prev.filter((x) => x.id !== st.id));
      showSuccess("Deleted", `Subtopic "${st.name}" deleted.`);
    } catch {
      showError("Error", "Failed to delete subtopic.");
    }
  };

  return (
    <div>
      <h1 className="text-[18px] font-bold lg:text-[22px]">Topics & Subtopics</h1>
      <p className="mt-1 text-[13px] text-text-muted">
        Manage the topic hierarchy. Questions are organized by Subject → Topic → Subtopic.
      </p>

      {/* Subject selector */}
      <div className="mt-6">
        <label className="mb-1 block text-[11px] font-semibold uppercase tracking-wider text-text-muted">
          Subject
        </label>
        <select
          value={selectedSubject}
          onChange={(e) => {
            setSelectedSubject(e.target.value);
            setSelectedTopic("");
          }}
          className="w-full max-w-xs rounded-lg border border-border bg-surface px-3 py-2.5 text-[14px] text-text outline-none"
        >
          <option value="">Select a subject…</option>
          {subjects.map((s) => (
            <option key={s.id} value={s.id}>
              {s.name}
            </option>
          ))}
        </select>
      </div>

      {selectedSubject ? (
        <div className="mt-6 grid gap-6 lg:grid-cols-2">
          {/* Topics */}
          <div className="rounded-xl border border-border bg-surface p-5">
            <h2 className="text-[15px] font-semibold">Topics</h2>
            <div className="mt-3 flex flex-col gap-1">
              {topics.length === 0 ? (
                <p className="text-[13px] text-text-muted">No topics yet.</p>
              ) : (
                topics.map((t) => (
                  <div
                    key={t.id}
                    className={`group flex items-center gap-2 rounded-lg px-3 py-2 transition-colors ${
                      selectedTopic === t.id
                        ? "bg-accent/15 text-accent-light"
                        : "text-text hover:bg-surface-high"
                    }`}
                  >
                    {editingTopicId === t.id ? (
                      <input
                        type="text"
                        value={editingTopicName}
                        onChange={(e) => setEditingTopicName(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") void saveEditTopic();
                          if (e.key === "Escape") setEditingTopicId(null);
                        }}
                        onBlur={() => void saveEditTopic()}
                        autoFocus
                        className="flex-1 rounded border border-accent bg-bg px-2 py-1 text-[13px] text-text outline-none"
                      />
                    ) : (
                      <button
                        type="button"
                        onClick={() => setSelectedTopic(t.id)}
                        className={`flex-1 text-left text-[13px] ${selectedTopic === t.id ? "font-semibold" : ""}`}
                      >
                        {t.name}
                      </button>
                    )}
                    {editingTopicId !== t.id ? (
                      <div className="flex gap-1 opacity-0 transition-opacity group-hover:opacity-100">
                        <button
                          type="button"
                          onClick={() => startEditTopic(t)}
                          className="rounded p-1 text-[12px] text-text-muted hover:bg-accent/15 hover:text-accent-light"
                          title="Rename"
                          aria-label="Rename"
                        >
                          <Pencil size={14} aria-hidden="true" />
                        </button>
                        <button
                          type="button"
                          onClick={() => void handleDeleteTopic(t)}
                          className="rounded p-1 text-[12px] text-text-muted hover:bg-negative/15 hover:text-negative"
                          title="Delete"
                          aria-label="Delete"
                        >
                          <Trash2 size={14} aria-hidden="true" />
                        </button>
                      </div>
                    ) : null}
                  </div>
                ))
              )}
            </div>

            <div className="mt-4 flex gap-2">
              <input
                type="text"
                value={newTopic}
                onChange={(e) => setNewTopic(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && void addTopic()}
                placeholder="New topic name…"
                className="flex-1 rounded-lg border border-border bg-bg px-3 py-2 text-[13px] text-text outline-none placeholder:text-text-muted"
              />
              <button
                type="button"
                disabled={saving || !newTopic.trim()}
                onClick={() => void addTopic()}
                className="rounded-lg bg-accent px-3 py-2 text-[13px] font-semibold text-bg disabled:opacity-50"
              >
                Add
              </button>
            </div>
          </div>

          {/* Subtopics */}
          <div className="rounded-xl border border-border bg-surface p-5">
            <h2 className="text-[15px] font-semibold">
              Subtopics
              {selectedTopic ? (
                <span className="ml-2 text-[12px] font-normal text-text-muted">
                  for {topics.find((t) => t.id === selectedTopic)?.name}
                </span>
              ) : null}
            </h2>

            {!selectedTopic ? (
              <p className="mt-3 text-[13px] text-text-muted">Select a topic first.</p>
            ) : (
              <>
                <div className="mt-3 flex flex-col gap-1">
                  {subtopics.length === 0 ? (
                    <p className="text-[13px] text-text-muted">No subtopics yet.</p>
                  ) : (
                    subtopics.map((st) => (
                      <div
                        key={st.id}
                        className="group flex items-center gap-2 rounded-lg px-3 py-2 text-[13px] text-text hover:bg-surface-high"
                      >
                        {editingSubtopicId === st.id ? (
                          <input
                            type="text"
                            value={editingSubtopicName}
                            onChange={(e) => setEditingSubtopicName(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") void saveEditSubtopic();
                              if (e.key === "Escape") setEditingSubtopicId(null);
                            }}
                            onBlur={() => void saveEditSubtopic()}
                            autoFocus
                            className="flex-1 rounded border border-accent bg-bg px-2 py-1 text-[13px] text-text outline-none"
                          />
                        ) : (
                          <span className="flex-1">{st.name}</span>
                        )}
                        {editingSubtopicId !== st.id ? (
                          <div className="flex gap-1 opacity-0 transition-opacity group-hover:opacity-100">
                            <button
                              type="button"
                              onClick={() => startEditSubtopic(st)}
                              className="rounded p-1 text-[12px] text-text-muted hover:bg-accent/15 hover:text-accent-light"
                              title="Rename"
                              aria-label="Rename"
                            >
                              <Pencil size={14} aria-hidden="true" />
                            </button>
                            <button
                              type="button"
                              onClick={() => void handleDeleteSubtopic(st)}
                              className="rounded p-1 text-[12px] text-text-muted hover:bg-negative/15 hover:text-negative"
                              title="Delete"
                              aria-label="Delete"
                            >
                              <Trash2 size={14} aria-hidden="true" />
                            </button>
                          </div>
                        ) : null}
                      </div>
                    ))
                  )}
                </div>

                <div className="mt-4 flex gap-2">
                  <input
                    type="text"
                    value={newSubtopic}
                    onChange={(e) => setNewSubtopic(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && void addSubtopic()}
                    placeholder="New subtopic name…"
                    className="flex-1 rounded-lg border border-border bg-bg px-3 py-2 text-[13px] text-text outline-none placeholder:text-text-muted"
                  />
                  <button
                    type="button"
                    disabled={saving || !newSubtopic.trim()}
                    onClick={() => void addSubtopic()}
                    className="rounded-lg bg-accent px-3 py-2 text-[13px] font-semibold text-bg disabled:opacity-50"
                  >
                    Add
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      ) : null}
    </div>
  );
}
